import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import Typography from "../../components/Typography/Typography";
import ReactMarkdown from "react-markdown";
import RelatedSearch from "../../components/RelatedSearch/RelatedSearch";

// (Your existing variants here)
const pageVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.005,
      staggerChildren: 0.001,
    },
  },
};

const charVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
};

const relatedSearchVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delayChildren: 0.2,
      staggerChildren: 0.1,
    },
  },
  exit: { opacity: 0, y: 20, transition: { duration: 0.5 } },
};

const searchItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
    },
  },
};

// New comprehensive special formatting function that handles both the text and the ReactMarkdown structure
const processSpecialFormattingComprehensive = (content) => {
  if (!content) return content;
  
  // Direct string processing
  if (typeof content === 'string') {
    // Updated regex to capture multiple numbers separated by commas
    const regex = /\{\{specialNumber:\s*((?:\d+(?:\s*,\s*\d+)*)+)\}\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(content)) !== null) {
      if (lastIndex < match.index) {
        parts.push(content.slice(lastIndex, match.index));
      }

      // Extract numbers from the captured group
      const numbersText = match[1];
      const numbers = numbersText.split(',').map(num => num.trim());
      
      // Create a span for each number
      const numberSpans = numbers.map((num, i) => (
        <span
          key={`special-${match.index}-${i}`}
          className="cursor-pointer inline-flex items-center justify-center h-4 w-4 mx-1 bg-gray-200 rounded-full align-middle"
        >
          <Typography size="8" color="gray-600">
            {num}
          </Typography>
        </span>
      ));
      
      // Add all number spans
      parts.push(...numberSpans);

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  }

  // Process React elements
  if (React.isValidElement(content)) {
    return React.cloneElement(content, {
      ...content.props,
      children: processSpecialFormattingComprehensive(content.props.children)
    });
  }

  // Process arrays (for nested content)
  if (Array.isArray(content)) {
    return content.map((item, index) => processSpecialFormattingComprehensive(item));
  }

  return content;
};

// Function to apply animations to text while preserving special formatting
const animateText = (text, isLatestResponse) => {
  if (!isLatestResponse || typeof text !== 'string') {
    return processSpecialFormattingComprehensive(text);
  }

  // First process any special formatting
  const processed = processSpecialFormattingComprehensive(text);
  
  // If the result is just a string, animate it character by character
  if (typeof processed === 'string') {
    return processed.split("").map((char, i) => (
      <motion.span key={i} variants={charVariants}>
        {char}
      </motion.span>
    ));
  }
  
  // If it's already an array with some processed elements, we need to animate the string parts
  if (Array.isArray(processed)) {
    return processed.map((part, index) => {
      if (typeof part === 'string') {
        return part.split("").map((char, i) => (
          <motion.span key={`${index}-${i}`} variants={charVariants}>
            {char}
          </motion.span>
        ));
      }
      return part;
    });
  }
  
  return processed;
};

const ResearchContent = ({ textExample = {}, searchFunction, isLatestResponse = true }) => {
  const [isVisible, setIsVisible] = useState(true);
  const contentRef = useRef(null);

  const handleSearch = (query) => {
    console.log("Search submitted from Research page:", query);
    searchFunction(query);
  };

  const scrollContentIntoView = () => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const animationProps = isLatestResponse
    ? { initial: "hidden", animate: "visible", variants: pageVariants }
    : {};

  return (
    <motion.div ref={contentRef} {...animationProps} className="flex flex-col gap-4 p-6 md:py-0 md:px-12">
      <ReactMarkdown
        components={{
          h1({ children, ...props }) {
            return (
              <motion.h1
                onAnimationStart={() => {
                  if (isLatestResponse) {
                    scrollContentIntoView();
                  }
                }}
                style={{ display: "inline-block" }}
                variants={charVariants}
              >
                <Typography size="48" family="serif" weight="sb">
                  {animateText(children, isLatestResponse)}
                </Typography>
              </motion.h1>
            );
          },
          h2({ children, ...props }) {
            return (
              <Typography size="20" weight="sb">
                {animateText(children, isLatestResponse)}
              </Typography>
            );
          },
          p({ children, ...props }) {
            return (
              <Typography size="16" weight="rg">
                {animateText(children, isLatestResponse)}
              </Typography>
            );
          },
          strong({ children, ...props }) {
            return (
              <Typography weight="sb">
                {animateText(children, isLatestResponse)}
              </Typography>
            );
          },
          li({ children, ...props }) {
            return (
              <li>
                <Typography size="16" weight="rg">
                  {animateText(children, isLatestResponse)}
                </Typography>
              </li>
            );
          },
          ul({ children, ...props }) {
            return (
              <ul className="list-disc pl-5 space-y-2">
                {children}
              </ul>
            );
          },
          ol({ children, ...props }) {
            return (
              <ol className="list-decimal pl-5 space-y-2">
                {children}
              </ol>
            );
          },
        }}
      >
        {textExample.content}
      </ReactMarkdown>

      <motion.div
        className={`flex flex-col gap-4 mt-9 ${isVisible ? 'block' : 'hidden' }`}
        variants={isLatestResponse ? relatedSearchVariants : {}}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onAnimationComplete={() => {
          if (!isLatestResponse) {
            setIsVisible(false);
          }
        }}
      >
        <Typography family="serif" size="32" weight="sb" variants={isLatestResponse ? searchItemVariants : {}}>
          Ricerche correlate
        </Typography>
        {(textExample.relatedSearches || []).map((search, index) => (
          <motion.div key={index} variants={isLatestResponse ? searchItemVariants : {}}>
            <RelatedSearch text={search.text} onClick={() => handleSearch(search.text)} />
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default ResearchContent;